import { useAtom } from "jotai";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { Copy, Download } from "@/assets/icons/ds";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useConfirmTwoFactorAuthenticationBackupCodes } from "@/services/auth";
import { AlertTypes, twoFactorBackupCodesAtom } from "@/utils/atoms";

import {
  copyCodesToClipboard,
  downloadCodes as _downloadCodes,
} from "./authenticator.utils";
import { NameConfirmationField } from "./NameConfirmationField";

export const AuthenticatorBackupCodeConfirm = ({
  successTarget,
}: {
  successTarget: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [backupCodes] = useAtom(twoFactorBackupCodesAtom);

  type AcceptTermsForm = {
    name: string;
    terms: boolean;
  };
  const formMethods = useForm<AcceptTermsForm>({
    defaultValues: {
      name: "",
      terms: false,
    },
    mode: "onSubmit",
  });

  const { mutateAsync: confirmBackupCodes } =
    useConfirmTwoFactorAuthenticationBackupCodes();

  const handleFormSubmit = formMethods.handleSubmit(() => {
    confirmBackupCodes()
      .then(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.success"),
          type: AlertTypes.success,
        });
        navigate(successTarget);
      })
      .catch(() => {
        formMethods.setError("name", {
          type: "manual",
          message: t("portal:authenticator.backupCodes.error"),
        });
      });
  });

  const showToast = useAlert();
  const copyCodes = () =>
    copyCodesToClipboard(backupCodes)
      .then(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.copySuccess"),
          type: AlertTypes.success,
        });
      })
      .catch(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.copyError"),
          type: AlertTypes.error,
        });
      });

  const downloadCodes = () => {
    _downloadCodes(backupCodes);
    showToast({
      text: t("portal:authenticator.backupCodes.downloadSuccess"),
      type: AlertTypes.success,
    });
  };

  if (!backupCodes.length) {
    return <Navigate to={portalRoutes.user.security.initialAppSetup} />;
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <ProfileCard
        maxWidth="none"
        title={t("portal:authenticator.backupCodes.title")}
        actions={
          <SubmitButton
            fullWidth={false}
            size="sm"
            label={t("portal:user-account.security.buttons.continue")}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <p className="whitespace-pre-line text-base text-foreground-primary">
            {t("portal:authenticator.backupCodes.description")}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-base font-semibold text-foreground-primary">
            {t("portal:authenticator.backupCodes.uniqueRecoveryCodes")}
          </h2>
          <div className="flex flex-col gap-2 rounded-md bg-surface-tertiary p-4">
            {backupCodes.map((code) => (
              <span
                key={code}
                className="text-base font-semibold text-foreground-primary"
              >
                {code}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={copyCodes}
              icon={<Copy className="h-4 w-4" />}
            >
              {t("portal:authenticator.backupCodes.copy")}
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={downloadCodes}
              icon={<Download className="h-4 w-4" />}
            >
              {t("portal:authenticator.backupCodes.download")}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-foreground-primary">
            {t("portal:authenticator.backupCodes.proceedWithSteps")}
          </h2>
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-semibold text-foreground-primary">
              {t("portal:authenticator.backupCodes.steps.1.title")}
            </h3>
            <p className="text-base text-foreground-primary">
              {t("portal:authenticator.backupCodes.steps.1.description")}
            </p>
          </div>
          <NameConfirmationField
            register={formMethods.register}
            errors={formMethods.formState.errors}
          />
          <Checkbox
            errors={formMethods.formState.errors}
            register={formMethods.register}
            name="terms"
            options={{
              required: t(
                "portal:authenticator.backupCodes.acceptTermsRequired",
              ),
            }}
            labelComponent={
              <Trans
                t={t}
                i18nKey="portal:authenticator.backupCodes.acceptTerms"
                components={{
                  a: (
                    <a
                      href={t("translation:Footer.termsConditions.url")}
                      target="_blank"
                      rel="noreferrer"
                      className="text-foreground-primary underline"
                    />
                  ),
                }}
              />
            }
          />
        </div>
      </ProfileCard>
    </form>
  );
};
